import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  InterviewService,
  BookingContext,
  InviteMatcherRequest,
  AvailableCoupon,
  RedeemCoinsPreview,
} from '../../../../shared/services/interview.service';
import { CommonService, UserRequestCategory } from '../../../../shared/services/common.service';
import { InterviewType } from '../../../../shared/models/interview-type';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { AvailableSlot, SuggestedDate } from '../../../../shared/models/panelist-availability';
import { ScheduleInterviewRequest } from '../../../../shared/models/interview-activity';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { CandidateService } from '../../../../shared/services/candidate.service';
import { PanelistExperienceService } from '../../../../shared/services/panelist-experience.service';
import { SkillsPreferencesService } from '../../../../shared/services/skills-preferences.service';
import { Skill } from '../../../../shared/models/panelist.model';
import { Department } from '../../../../shared/models/user.model';
import {
  WorkExperienceRequest,
  WorkExperienceResponse,
  PrimarySkillDto,
} from '../../../../shared/models/profile.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoaderOverlayComponent } from '../../../../shared/components/loader-overlay/loader-overlay.component';
import { ChooseSlotComponent } from '../../../../shared/components/choose-slot/choose-slot.component';
import { ThemeService } from '../../../../shared/services/theme.service';

declare var Cashfree: any;

@Component({
  selector: 'app-book-interview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ToastModule,
    SelectModule,
    MultiSelectModule,
    DialogModule,
    SkeletonModule,
    TooltipModule,
    CheckboxModule,
    LoaderOverlayComponent,
    PrimeTemplate,
    ChooseSlotComponent,
  ],
  templateUrl: './book-interview.component.html',
  styleUrl: './book-interview.component.scss',
  animations: [
    trigger('popupAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('180ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('140ms ease-in', style({ opacity: 0, transform: 'translateY(20px)' })),
      ]),
    ]),
  ],
})
export class BookInterviewComponent implements OnInit {
  private interviewService = inject(InterviewService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private commonService = inject(CommonService);
  private candidateService = inject(CandidateService);
  private experienceService = inject(PanelistExperienceService);
  private skillsPreferencesService = inject(SkillsPreferencesService);
  private http = inject(HttpClient);
  private themeService = inject(ThemeService);

  /** Only used in the public invite-flow header (no dashboard to link back
   *  to, so the header shows the logo + a theme toggle instead). */
  readonly darkMode = this.themeService.isDark;
  readonly logoUrl = this.themeService.logoUrl;

  toggleDark(): void {
    this.themeService.toggle();
  }

  readonly MAX_FOCUS_SKILLS = 5;
  currentStep: 1 | 2 | 3 = 1;

  readonly PANEL_LEVEL_PRICE: Record<string, number> = {
    Certified: 1199,
    Standard: 899,
    Elite: 1599,
  };
  readonly ALL_PANEL_LEVELS = ['Standard', 'Certified', 'Elite'];

  readonly TIER_INFO: { level: string; description: string }[] = [
    { level: 'Standard', description: 'Vetted industry interviewer · report in 48h' },
    { level: 'Certified', description: 'Certified panel · structured scorecard' },
    { level: 'Elite', description: 'Senior expert · same-day detailed report' },
  ];

  realPanelLevelPrice: Record<string, number | null> = {
    Standard: null,
    Certified: null,
    Elite: null,
  };

  priceForLevel = (level: string): number => {
    return this.realPanelLevelPrice[level] ?? this.PANEL_LEVEL_PRICE[level];
  };

  private readonly cashfreeMode: 'sandbox' | 'production' = 'sandbox';
  payingForInterview = false;

  goToStep(step: 1 | 2 | 3) {
    if (step > this.currentStep && !this.canAdvanceTo(step)) return;
    this.currentStep = step;
  }

  private canAdvanceTo(step: 1 | 2 | 3): boolean {
    if (step === 2) return this.canContinueFromStep1;
    if (step === 3) return this.isScheduleValid;
    return true;
  }

  get canContinueFromStep1(): boolean {
    if (this.isInviteFlow) return !!this.selectedType;
    if (this.noPrimarySkill) return false;
    if (!this.candidateDepartment || !this.selectedPrimarySkillForBooking) return false;
    return this.selectedSkills.length > 0;
  }

  continueToStep2() {
    if (!this.canContinueFromStep1) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Focus Skills',
        detail: this.isInviteFlow
          ? 'Please select a focus area'
          : 'Please select at least one focus skill',
      });
      return;
    }
    this.currentStep = 2;
    this.loadAvailableDates();
    if (!this.selectedDate) {
      const today = new Date();
      this.currentMonth = today.getMonth();
      this.currentYear = today.getFullYear();
      this.selectedDate = today.getDate();
      this.loadSlots();
    }
  }

  backToStep1() {
    this.currentStep = 1;
  }

  backToStep2() {
    this.currentStep = 2;
  }

  get overlayVisible(): boolean {
    return this.scheduling || this.payingForInterview;
  }

  get overlayMessage(): string {
    if (this.scheduling) return 'Booking your interview…';
    return 'Processing payment…';
  }

  step1Loading = false;

  profileIncomplete = false;
  missingDepartment = false;
  candidateDepartment: string | null = null;

  departmentOptions: Department[] = [];
  selectedDepartmentOption: Department | null = null;
  savingDepartment = false;

  myPrimarySkills: PrimarySkillDto[] = [];
  selectedPrimarySkillForBooking: PrimarySkillDto | null = null;

  get noPrimarySkill(): boolean {
    return this.myPrimarySkills.length === 0;
  }

  get primarySkillId(): number | null {
    return this.selectedPrimarySkillForBooking?.primarySkillId ?? null;
  }

  get primarySkillName(): string {
    return this.selectedPrimarySkillForBooking?.primarySkillName ?? '';
  }

  comparePrimarySkillById(a: PrimarySkillDto | null, b: PrimarySkillDto | null): boolean {
    return (a?.primarySkillId ?? null) === (b?.primarySkillId ?? null);
  }

  experiences: WorkExperienceResponse[] = [];
  showAddExpDialog = false;
  newExp: Partial<WorkExperienceRequest> = {};
  savingExperience = false;

  editingExpId: number | null = null;
  showDeleteExpConfirm = false;
  confirmDeleteExpId: number | null = null;
  readonly currentYears = Array.from({ length: 40 }, (_, i) => {
    const yr = new Date().getFullYear() - i;
    return { label: String(yr), value: yr };
  });

  readonly expMonths = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  companyOptions: { id: number; name?: string; title?: string; industryType?: string }[] = [];
  jobTitleOptions: { id: number; name?: string; title?: string; industryType?: string }[] = [];
  industryOptions: { id: number; name?: string; title?: string; industryType?: string }[] = [];
  employmentTypeOptions: { id: number; name?: string; title?: string; industryType?: string }[] =
    [];

  showAddLookupDialog = false;
  addLookupTarget:
    | 'company'
    | 'role'
    | 'industry'
    | 'employmentType'
    | 'department'
    | 'skill'
    | null = null;
  addLookupTitle = '';
  addLookupPlaceholder = '';
  addLookupValue = '';
  savingLookup = false;

  isInviteFlow = false;
  token = '';
  bookingContext: BookingContext | null = null;
  tokenError = '';
  booked = false;
  /** Set the instant Cashfree confirms a charge went through, and never
   *  reset back to false afterwards (even if the booking API call that
   *  follows then fails) -- keeps "Pay & Book" permanently disabled so a
   *  candidate can never pay twice for the same slot, whether the booking
   *  that follows succeeds or a support ticket ends up being needed. */
  paymentCompleted = false;

  availableDates = new Set<number>();

  months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  currentMonth = new Date().getMonth();
  currentYear = new Date().getFullYear();
  selectedDate = 0;

  focusAreas: InterviewType[] = [];
  selectedType?: InterviewType;
  childSkills: Skill[] = [];
  selectedSkills: Skill[] = [];
  private savedFocusSkillIds: Set<number> = new Set();
  focusSkillsEditMode = false;

  availableSlots: AvailableSlot[] = [];
  selectedSlotTime: string | null = null;

  loadingSlots = false;
  loadingDates = false;

  suggestedDates: SuggestedDate[] = [];
  loadingSuggestions = false;

  private readonly MIN_LEAD_MINUTES = 30;

  private isSlotTimePast(slotLabel: string): boolean {
    if (!this.isToday(this.selectedDate)) return false;
    const startPart = slotLabel.split('-')[0]?.trim();
    if (!startPart) return false;
    const [hours, minutes] = startPart.split(':').map((n) => parseInt(n, 10));
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;
    const slotStart = new Date(
      this.currentYear,
      this.currentMonth,
      this.selectedDate,
      hours,
      minutes,
    );
    return slotStart.getTime() <= Date.now() + this.MIN_LEAD_MINUTES * 60 * 1000;
  }

  get visibleAvailableSlots(): AvailableSlot[] {
    return this.availableSlots
      .filter((s) => !this.isSlotTimePast(s.slot))
      .sort((a, b) => this.slotStartMinutes(a.slot) - this.slotStartMinutes(b.slot));
  }

  private slotStartMinutes(slotLabel: string): number {
    const startPart = slotLabel.split('-')[0]?.trim();
    if (!startPart) return 0;
    const [hours, minutes] = startPart.split(':').map((n) => parseInt(n, 10));
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
    return hours * 60 + minutes;
  }

  get slotsForSelectedLevel(): AvailableSlot[] {
    if (this.isInviteFlow) return this.visibleAvailableSlots;
    return this.visibleAvailableSlots.filter((s) =>
      (s.availablePanelLevels ?? []).includes(this.selectedPanelLevel),
    );
  }

  get selectedSlot(): AvailableSlot | undefined {
    return this.slotsForSelectedLevel.find((s) => s.slot === this.selectedSlotTime);
  }

  readonly PANEL_LEVEL_COST: Record<string, number | undefined> = {
    Certified: 1,
    Standard: 2,
    Elite: 3,
  };
  selectedPanelLevel = 'Certified';
  creditsRemaining: number | null = null;
  planInterviewCredits: number | null = null;

  get selectedPanelLevelCost(): number {
    return this.PANEL_LEVEL_COST[this.selectedPanelLevel] ?? 1;
  }

  showConfirmDialog = false;
  scheduling = false;

  showRequestPopup = false;
  requestDate = '';
  requestTime = '';

  ngOnInit() {
    this.loadCashfreeScript();

    const rawToken = this.route.snapshot.queryParamMap.get('token');
    if (rawToken) {
      this.isInviteFlow = true;
      this.token = rawToken;
      sessionStorage.setItem('invite_token', rawToken);
      this.loadBookingContext();
    } else {
      this.loadStep1Config();
    }
  }

  loadStep1Config() {
    this.step1Loading = true;

    this.skillsPreferencesService.getSkillsPreferences().subscribe({
      next: (prefs) => {
        this.experiences = this.sortExperiences(prefs.experience || []);

        const deptName = prefs.department?.name || null;
        this.candidateDepartment = deptName;
        this.missingDepartment = !deptName;
        if (deptName) this.ensureDepartmentsLoaded();

        // primarySkills is already in the prefs response — no extra API call needed
        this.myPrimarySkills =
          prefs.primarySkills ?? (prefs.primarySkill ? [prefs.primarySkill] : []);
        this.profileIncomplete = this.noPrimarySkill;

        if (this.noPrimarySkill) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Profile Incomplete',
            detail: 'Please add a Primary Skill in your profile before booking an interview.',
            life: 8000,
            sticky: false,
          });
          this.step1Loading = false;
          return;
        }

        this.selectedPrimarySkillForBooking = this.myPrimarySkills[0];
        this.lastLoadedPrimarySkillId = this.myPrimarySkills[0].primarySkillId;

        // Prefer childSkills embedded in the primary skill object (new API);
        // fall back to the top-level prefs.skills for older responses.
        const savedSkills = (this.myPrimarySkills[0]?.childSkills ?? prefs.skills ?? []) as Skill[];
        this.savedFocusSkillIds = new Set(savedSkills.map((s) => s.id));
        if (savedSkills.length) {
          this.selectedSkills = savedSkills.slice(0, this.MAX_FOCUS_SKILLS);
          this.loadSlots();
        }

        this.step1Loading = false;
      },
      error: () => {
        this.profileIncomplete = false;
        this.myPrimarySkills = [];
        this.selectedPrimarySkillForBooking = null;
        this.step1Loading = false;
      },
    });
  }

  onPrimarySkillForBookingChange(): void {
    this.applyPrimarySkillSelection();
    this.loadSlots();
  }

  private lastLoadedPrimarySkillId: number | null = null;

  private pendingPrimarySkillFetch: {
    primarySkillId: number;
    onDoneFns: Array<() => void>;
  } | null = null;

  private applyPrimarySkillSelection(onDone?: () => void): void {
    const primarySkillId = this.selectedPrimarySkillForBooking?.primarySkillId;

    if (!primarySkillId) {
      this.selectedSkills = [];
      this.childSkills = [];
      this.savedFocusSkillIds = new Set();
      this.pendingPrimarySkillFetch = null;
      this.lastLoadedPrimarySkillId = null;
      onDone?.();
      return;
    }

    if (primarySkillId === this.lastLoadedPrimarySkillId) {
      onDone?.();
      return;
    }

    if (this.pendingPrimarySkillFetch?.primarySkillId === primarySkillId) {
      if (onDone) this.pendingPrimarySkillFetch.onDoneFns.push(onDone);
      return;
    }

    this.selectedSkills = [];
    this.childSkills = [];
    this.savedFocusSkillIds = new Set();
    this.childSkillsLoaded = false;
    const pending = { primarySkillId, onDoneFns: onDone ? [onDone] : [] };
    this.pendingPrimarySkillFetch = pending;

    const settle = () => {
      if (this.pendingPrimarySkillFetch === pending) this.pendingPrimarySkillFetch = null;
      pending.onDoneFns.forEach((fn) => fn());
    };

    this.experienceService.getChildSkills(primarySkillId).subscribe({
      next: (childSkills) => {
        this.childSkills = childSkills;
        this.preselectFocusSkillsFromSaved(primarySkillId);
        this.lastLoadedPrimarySkillId = primarySkillId;
        settle();
      },
      error: (err) => {
        console.error('Failed to load child skills', err);
        settle();
      },
    });
  }

  onDepartmentOptionChange(): void {
    if (!this.selectedDepartmentOption) return;
    this.savingDepartment = true;
    this.skillsPreferencesService
      .updateDepartmentDomain({ departmentId: this.selectedDepartmentOption.id })
      .subscribe({
        next: () => {
          this.savingDepartment = false;
          this.candidateDepartment = this.selectedDepartmentOption!.departmentName;
          this.missingDepartment = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Department Saved',
            detail: 'Updated in your profile.',
            life: 2500,
          });
        },
        error: () => {
          this.savingDepartment = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Save Failed',
            detail: 'Could not update your department. Please try again.',
            life: 4000,
          });
        },
      });
  }

  loadBookingContext() {
    this.step1Loading = true;
    this.interviewService.getBookingContext(this.token).subscribe({
      next: (ctx) => {
        this.step1Loading = false;
        if (!ctx.valid) {
          this.tokenError = ctx.message || 'This invite link is invalid or has expired.';
          return;
        }
        this.bookingContext = ctx;
        this.candidateDepartment = ctx.department || null;
        this.departmentOptions = ctx.department
          ? [{ id: 0, departmentName: ctx.department, description: '' }]
          : [];
        this.selectedDepartmentOption = this.departmentOptions[0] ?? null;

        this.myPrimarySkills = ctx.primarySkillName
          ? [
              {
                primarySkillId: 0,
                primarySkillName: ctx.primarySkillName,
                primarySkillUpdatedAt: null,
              },
            ]
          : [];
        this.selectedPrimarySkillForBooking = this.myPrimarySkills[0] ?? null;

        this.selectedSkills = (ctx.skills || [])
          .filter((skill) => skill !== ctx.primarySkillName)
          .map((skill, i) => ({ id: i, skill }));

        this.focusAreas = [{ id: ctx.interviewTypeId, name: ctx.interviewTypeName }];
        this.selectedType = this.focusAreas[0];
      },
      error: () => {
        this.step1Loading = false;
        this.tokenError = 'Failed to load your invitation details. Please try refreshing.';
      },
    });
  }

  loadAvailableDates() {
    this.loadingDates = true;
    const skillIds = this.isInviteFlow ? undefined : this.selectedSkills.map((s) => s.id);
    const type = this.isInviteFlow ? this.selectedType?.name : undefined;
    this.interviewService
      .getAvailableDatesForMonth(
        this.currentYear,
        this.currentMonth + 1,
        type,
        this.candidateDepartment || undefined,
        skillIds,
      )
      .subscribe({
        next: (days) => {
          this.availableDates = new Set(days);
          this.loadingDates = false;
        },
        error: () => {
          this.availableDates = new Set();
          this.loadingDates = false;
        },
      });
  }

  clearDate() {
    this.selectedDate = 0;
    this.availableSlots = [];
    this.selectedSlotTime = null;
    this.expandedSlotTime = null;
  }

  selectDate(date: number) {
    this.selectedDate = date;
    this.availableSlots = [];
    this.selectedSlotTime = null;
    this.expandedSlotTime = null;
    this.loadSlots();
  }

  isToday(date: number): boolean {
    const today = new Date();
    return (
      date !== 0 &&
      date === today.getDate() &&
      this.currentMonth === today.getMonth() &&
      this.currentYear === today.getFullYear()
    );
  }

  expandedSlotTime: string | null = null;

  slotStartTime(slot: string): string {
    const start = slot.split(' - ')[0]?.trim() ?? slot;
    return this.to12HourClock(start);
  }

  private to12HourClock(time: string): string {
    const match = /^(\d{1,2}):(\d{2})/.exec(time);
    if (!match) return time;
    const minutes = match[2];
    let hours = parseInt(match[1], 10);
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes} ${period}`;
  }

  get footerSelectedTimeLabel(): string | null {
    const time = this.selectedSlotTime ?? this.expandedSlotTime;
    return time ? this.slotStartTime(time) : null;
  }

  get footerHintText(): string {
    if (this.isScheduleValid) return 'ready to continue';
    if (this.selectedSlotTime || this.expandedSlotTime) return 'pick a tier to continue';
    return 'Pick a time slot to continue';
  }

  isSlotExpanded(slot: AvailableSlot): boolean {
    return this.expandedSlotTime === slot.slot;
  }

  get expandedSlot(): AvailableSlot | null {
    if (!this.expandedSlotTime) return null;
    return this.visibleAvailableSlots.find((s) => s.slot === this.expandedSlotTime) ?? null;
  }

  get slotRows(): AvailableSlot[][] {
    const slots = this.visibleAvailableSlots;
    const rows: AvailableSlot[][] = [];
    for (let i = 0; i < slots.length; i += 3) {
      rows.push(slots.slice(i, i + 3));
    }
    return rows;
  }

  expandedSlotInRow(row: AvailableSlot[]): AvailableSlot | null {
    if (!this.expandedSlotTime) return null;
    return row.find((s) => s.slot === this.expandedSlotTime) ?? null;
  }

  isSlotCommitted(slot: AvailableSlot): boolean {
    return this.selectedSlotTime === slot.slot;
  }

  toggleSlotExpand(slot: AvailableSlot): void {
    if (slot.booked) return;
    this.expandedSlotTime = this.expandedSlotTime === slot.slot ? null : slot.slot;
  }

  selectInviteSlot(slot: AvailableSlot): void {
    this.selectedSlotTime = slot.slot;
  }

  chooseTierForSlot(slot: AvailableSlot, level: string): void {
    if (!(slot.availablePanelLevels ?? []).includes(level)) return;
    this.selectedPanelLevel = level;
    this.selectedSlotTime = slot.slot;
  }

  private totalExpertsForSlot(slot: AvailableSlot): number {
    const levels = slot.availablePanelLevels ?? [];
    if (slot.expertCountByLevel) {
      return levels.reduce((sum, l) => sum + (slot.expertCountByLevel?.[l] ?? 0), 0);
    }
    return levels.length;
  }

  slotUrgencyBadge(slot: AvailableSlot): { text: string; variant: 'fast' | 'few' } | null {
    const total = this.totalExpertsForSlot(slot);
    if (total <= 0) return null;
    if (total <= 2)
      return { text: total === 1 ? 'Only 1 left' : `Only ${total} left`, variant: 'few' };
    if (total <= 5) return { text: 'Booking fast', variant: 'fast' };
    return null;
  }

  levelIcon(level: string): string {
    switch (level) {
      case 'Certified':
        return 'pi-verified';
      case 'Elite':
        return 'pi-crown';
      default:
        return 'pi-user';
    }
  }

  onFocusSkillsChange(): void {
    if (this.selectedSkills.length > this.MAX_FOCUS_SKILLS) {
      this.selectedSkills = this.selectedSkills.slice(0, this.MAX_FOCUS_SKILLS);
      this.messageService.add({
        severity: 'warn',
        summary: 'Limit Reached',
        detail: `You can select up to ${this.MAX_FOCUS_SKILLS} focus skills.`,
        life: 3000,
      });
    }
    this.persistFocusSkills();
    this.loadSlots();
  }

  removeFocusSkill(skill: Skill): void {
    this.selectedSkills = this.selectedSkills.filter((s) => s !== skill);
    this.persistFocusSkills();
    this.loadSlots();
  }

  private persistFocusSkills(): void {
    const primarySkillId = this.primarySkillId;
    if (!primarySkillId) return;
    const currentIds = new Set(this.selectedSkills.map((s) => s.id));
    const toAdd = [...currentIds].filter((id) => !this.savedFocusSkillIds.has(id));
    const toRemove = [...this.savedFocusSkillIds].filter((id) => !currentIds.has(id));
    if (toAdd.length === 0 && toRemove.length === 0) return;
    const requests: Observable<string>[] = [
      ...toAdd.map((id) => this.candidateService.addPrimarySkillChild(primarySkillId, id)),
      ...toRemove.map((id) => this.candidateService.removePrimarySkillChild(primarySkillId, id)),
    ];
    forkJoin(requests).subscribe({
      next: () => {
        this.savedFocusSkillIds = currentIds;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Save Failed',
          detail: 'Could not save your focus skills. Please try again.',
          life: 3500,
        });
      },
    });
  }

  private preselectFocusSkillsFromSaved(primarySkillId: number, onDone?: () => void): void {
    this.candidateService.getPrimarySkillChildren(primarySkillId).subscribe({
      next: (saved) => {
        const savedIds = new Set((saved || []).map((s) => s.id));
        this.savedFocusSkillIds = savedIds;
        if (this.selectedSkills.length > 0 || !savedIds.size) {
          onDone?.();
          return;
        }
        const matches = this.childSkills.filter((s) => savedIds.has(s.id));
        if (matches.length) {
          this.selectedSkills = matches.slice(0, this.MAX_FOCUS_SKILLS);
          this.loadSlots();
        }
        onDone?.();
      },
      error: () => onDone?.(),
    });
  }

  private sortExperiences(list: WorkExperienceResponse[]): WorkExperienceResponse[] {
    return [...list].sort((a, b) => {
      if (a.current !== b.current) return a.current ? -1 : 1;
      return (b.endYear ?? b.startYear) - (a.endYear ?? a.startYear);
    });
  }

  expDurationLabel(exp: WorkExperienceResponse): string {
    const start = this.formatMonthYear(exp.startYear, exp.startMonth);
    const end = exp.current ? 'Present' : this.formatMonthYear(exp.endYear, exp.endMonth);
    return end ? `${start} – ${end}` : start;
  }

  private formatMonthYear(
    year: number | null | undefined,
    month: number | null | undefined,
  ): string {
    if (!year) return '';
    const monthLabel = month ? this.expMonths.find((m) => m.value === month)?.label : null;
    return monthLabel ? `${monthLabel.slice(0, 3)} ${year}` : `${year}`;
  }

  get totalExperienceLabel(): string {
    if (!this.experiences.length) return 'None added yet';

    const totalMonths = this.experiences.reduce(
      (sum, exp) => sum + this.experienceDurationMonths(exp),
      0,
    );

    if (totalMonths <= 0) return 'Less than a month';

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const parts: string[] = [];
    if (years > 0) parts.push(`${years} yr${years === 1 ? '' : 's'}`);
    if (months > 0) parts.push(`${months} mo${months === 1 ? '' : 's'}`);
    return parts.join(' ');
  }

  private experienceDurationMonths(exp: WorkExperienceResponse): number {
    if (!exp.startYear) return 0;
    const startMonth = exp.startMonth ?? 1;

    const now = new Date();
    const endYear = exp.current ? now.getFullYear() : (exp.endYear ?? exp.startYear);
    const endMonth = exp.current ? now.getMonth() + 1 : (exp.endMonth ?? startMonth);

    const months = (endYear - exp.startYear) * 12 + (endMonth - startMonth);
    return Math.max(months, 0);
  }

  private childSkillsLoaded = false;
  private departmentsLoaded = false;
  private expLookupsLoaded = false;

  ensureChildSkillsLoaded(): void {
    if (this.childSkillsLoaded || !this.selectedPrimarySkillForBooking) return;
    this.childSkillsLoaded = true;
    this.experienceService
      .getChildSkills(this.selectedPrimarySkillForBooking.primarySkillId)
      .subscribe({
        next: (skills) => {
          this.childSkills = skills;
        },
        error: () => {},
      });
  }

  ensureDepartmentsLoaded(): void {
    if (this.departmentsLoaded) return;
    this.departmentsLoaded = true;
    this.commonService.getDepartments().subscribe({
      next: (depts) => {
        this.departmentOptions = depts;
        if (this.candidateDepartment) {
          this.selectedDepartmentOption =
            depts.find(
              (d) => d.departmentName.toLowerCase() === this.candidateDepartment!.toLowerCase(),
            ) ?? null;
        }
      },
      error: () => {},
    });
  }

  private loadExpLookups(): void {
    if (this.expLookupsLoaded) return;
    this.expLookupsLoaded = true;
    forkJoin({
      companies: this.skillsPreferencesService.getAllCompanies().pipe(catchError(() => of([]))),
      jobTitles: this.skillsPreferencesService.getAllJobTitles().pipe(catchError(() => of([]))),
      industries: this.skillsPreferencesService.getAllIndustries().pipe(catchError(() => of([]))),
      employmentTypes: this.skillsPreferencesService
        .getAllEmploymentTypes()
        .pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ companies, jobTitles, industries, employmentTypes }) => {
        this.companyOptions = companies;
        this.jobTitleOptions = jobTitles;
        this.industryOptions = industries;
        this.employmentTypeOptions = employmentTypes;
      },
      error: () => {},
    });
  }

  openAddExperienceDialog(): void {
    this.loadExpLookups();
    this.editingExpId = null;
    this.newExp = { current: false };
    this.showAddExpDialog = true;
  }

  openEditExperienceDialog(exp: WorkExperienceResponse): void {
    this.loadExpLookups();
    this.editingExpId = exp.id;
    this.newExp = {
      companyName: exp.companyName,
      role: exp.role,
      industry: exp.industry ?? '',
      employmentType: exp.employmentType ?? '',
      startYear: exp.startYear,
      startMonth: exp.startMonth ?? undefined,
      endYear: exp.endYear ?? undefined,
      endMonth: exp.endMonth ?? undefined,
      current: exp.current,
      description: exp.description ?? '',
    };
    this.showAddExpDialog = true;
  }

  requestDeleteExperience(id: number): void {
    this.confirmDeleteExpId = id;
    this.showDeleteExpConfirm = true;
  }

  confirmDeleteExperience(): void {
    const id = this.confirmDeleteExpId;
    if (id === null) return;
    this.showDeleteExpConfirm = false;
    this.confirmDeleteExpId = null;
    this.skillsPreferencesService.deleteExperience(id).subscribe({
      next: () => {
        this.experiences = this.experiences.filter((e) => e.id !== id);
        this.messageService.add({
          severity: 'success',
          summary: 'Experience Removed',
          detail: 'Work experience removed.',
          life: 2000,
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Remove Failed',
          detail: 'Could not remove experience. Please try again.',
          life: 3000,
        });
      },
    });
  }

  openAddLookupDialog(
    target: 'company' | 'role' | 'industry' | 'employmentType' | 'department' | 'skill',
  ): void {
    this.addLookupTarget = target;
    this.addLookupValue = '';
    this.savingLookup = false;
    switch (target) {
      case 'company':
        this.addLookupTitle = 'Add New Company';
        this.addLookupPlaceholder = 'e.g. Acme Corp';
        break;
      case 'role':
        this.addLookupTitle = 'Add New Job Title';
        this.addLookupPlaceholder = 'e.g. Software Engineer';
        break;
      case 'industry':
        this.addLookupTitle = 'Add New Industry';
        this.addLookupPlaceholder = 'e.g. Technology';
        break;
      case 'employmentType':
        this.addLookupTitle = 'Add New Employment Type';
        this.addLookupPlaceholder = 'e.g. Full-time';
        break;
      case 'department':
        this.addLookupTitle = 'Suggest a Department';
        this.addLookupPlaceholder = 'e.g. Civil Engineering';
        break;
      case 'skill':
        this.addLookupTitle = 'Suggest a Skill';
        this.addLookupPlaceholder = 'e.g. Kubernetes';
        break;
    }
    this.showAddLookupDialog = true;
  }

  private readonly addLookupCategoryMap: Record<
    'company' | 'role' | 'industry' | 'employmentType' | 'department' | 'skill',
    UserRequestCategory
  > = {
    company: 'COMPANY',
    role: 'ROLE',
    industry: 'INDUSTRY',
    employmentType: 'EMPLOYMENT_TYPE',
    department: 'DEPARTMENT',
    skill: 'SKILL',
  };

  confirmAddLookup(): void {
    const value = this.addLookupValue.trim();
    if (!value || !this.addLookupTarget) return;
    this.confirmSubmitUserRequest(this.addLookupTarget, value);
  }

  private confirmSubmitUserRequest(
    target: 'company' | 'role' | 'industry' | 'employmentType' | 'department' | 'skill',
    value: string,
  ): void {
    this.savingLookup = true;
    const category = this.addLookupCategoryMap[target];
    this.commonService.submitUserRequest(category, value).subscribe({
      next: () => {
        this.savingLookup = false;
        this.showAddLookupDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Suggestion Sent',
          detail: `Thanks! We'll review "${value}" and add it if it fits.`,
          life: 4000,
        });
      },
      error: () => {
        this.savingLookup = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Could not send suggestion',
          detail: 'Something went wrong. Please try again.',
          life: 5000,
        });
      },
    });
  }

  get isNewExpValid(): boolean {
    const e = this.newExp;
    if (!e.companyName?.trim() || !e.role?.trim()) return false;
    if (!e.startYear) return false;
    if (!e.current && e.endYear && e.endYear < e.startYear) return false;
    if (this.newExpOverlapError) return false;
    return true;
  }

  get newExpDateError(): string {
    const e = this.newExp;
    if (e.startYear && !e.current && e.endYear && e.endYear < e.startYear) {
      return 'End year cannot be before start year.';
    }
    return this.newExpOverlapError;
  }

  private expMonthIndex(
    year: number | null | undefined,
    month: number | null | undefined,
    isEnd: boolean,
  ): number {
    if (year == null) return isEnd ? -Infinity : Infinity;
    return year * 12 + (month ?? (isEnd ? 12 : 1));
  }

  private expRangeOverlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
    return aStart <= bEnd && bStart <= aEnd;
  }

  get newExpOverlapError(): string {
    const e = this.newExp;
    if (!e.startYear) return '';
    const aStart = this.expMonthIndex(e.startYear, e.startMonth, false);
    const aEnd = e.current
      ? Infinity
      : this.expMonthIndex(e.endYear ?? e.startYear, e.endMonth, true);

    const overlaps = this.experiences.some((exp) => {
      if (this.editingExpId != null && exp.id === this.editingExpId) return false;
      const bStart = this.expMonthIndex(exp.startYear, exp.startMonth, false);
      const bEnd = exp.current
        ? Infinity
        : this.expMonthIndex(exp.endYear ?? exp.startYear, exp.endMonth, true);
      return this.expRangeOverlaps(aStart, aEnd, bStart, bEnd);
    });
    return overlaps ? 'These dates overlap with another work experience entry.' : '';
  }

  confirmAddExperience(): void {
    if (!this.isNewExpValid) return;
    const payload: WorkExperienceRequest = {
      companyName: this.newExp.companyName!.trim(),
      role: this.newExp.role!.trim(),
      industry: this.newExp.industry?.trim() || undefined,
      employmentType: this.newExp.employmentType?.trim() || undefined,
      startYear: this.newExp.startYear!,
      startMonth: this.newExp.startMonth ?? undefined,
      endYear: this.newExp.current ? undefined : this.newExp.endYear,
      endMonth: this.newExp.current ? undefined : (this.newExp.endMonth ?? undefined),
      current: !!this.newExp.current,
      description: this.newExp.description?.trim() || undefined,
    };
    this.savingExperience = true;

    const isEdit = this.editingExpId != null;
    const request$ = isEdit
      ? this.skillsPreferencesService.updateExperience(this.editingExpId!, payload)
      : this.skillsPreferencesService.addExperience(payload);

    request$.subscribe({
      next: (saved) => {
        this.savingExperience = false;
        this.showAddExpDialog = false;
        this.experiences = isEdit
          ? this.sortExperiences(this.experiences.map((e) => (e.id === saved.id ? saved : e)))
          : this.sortExperiences([...this.experiences, saved]);
        this.editingExpId = null;
        this.messageService.add({
          severity: 'success',
          summary: isEdit ? 'Experience Updated' : 'Experience Added',
          detail: 'Saved to your profile.',
          life: 2500,
        });
      },
      error: () => {
        this.savingExperience = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Save Failed',
          detail: 'Could not save this experience. Please try again.',
          life: 4000,
        });
      },
    });
  }

  closeFocusSkillsEdit(): void {
    if (this.selectedSkills.length > 0) {
      this.focusSkillsEditMode = false;
    }
  }

  loadSlots() {
    const hasFocusSelection = this.isInviteFlow
      ? !!this.selectedType
      : this.selectedSkills.length > 0;
    if (!this.selectedDate || !hasFocusSelection) return;
    if (this.profileIncomplete) return;

    this.suggestedDates = [];
    this.loadingSlots = true;
    this.availableSlots = [];
    this.selectedSlotTime = null;
    this.expandedSlotTime = null;
    const d = new Date(this.currentYear, this.currentMonth, this.selectedDate);
    const formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const skillIds = this.isInviteFlow ? undefined : this.selectedSkills.map((s) => s.id);
    const type = this.isInviteFlow ? this.selectedType?.name : undefined;
    const primarySkillId = this.isInviteFlow ? undefined : (this.primarySkillId ?? undefined);
    const panelLevel = this.isInviteFlow ? this.selectedPanelLevel : undefined;

    this.interviewService
      .getAvailableSlots(
        formattedDate,
        type,
        this.candidateDepartment || undefined,
        skillIds,
        primarySkillId,
        panelLevel,
      )
      .subscribe({
        next: (res: AvailableSlot[]) => {
          this.loadingSlots = false;
          this.availableSlots = res;
          // if (this.slotsForSelectedLevel.length === 0) {
          //   this.loadSuggestedDates(formattedDate);
          // }
          if (this.visibleAvailableSlots.length === 0) {
            this.loadSuggestedDates(formattedDate);
          }
        },
        error: () => {
          this.loadingSlots = false;
          this.availableSlots = [];
          this.loadSuggestedDates(formattedDate);
        },
      });
  }

  loadSuggestedDates(fromDate: string) {
    this.loadingSuggestions = true;
    const skillIds = this.isInviteFlow ? undefined : this.selectedSkills.map((s) => s.id);
    const type = this.isInviteFlow ? this.selectedType?.name : undefined;
    const primarySkillId = this.isInviteFlow ? undefined : (this.primarySkillId ?? undefined);

    this.interviewService
      .getSuggestedDates(
        fromDate,
        2,
        type,
        this.candidateDepartment || undefined,
        skillIds,
        primarySkillId,
      )
      .subscribe({
        next: (res) => {
          this.suggestedDates = res;
          this.loadingSuggestions = false;
        },
        error: () => {
          this.suggestedDates = [];
          this.loadingSuggestions = false;
        },
      });
  }

  pickSuggestedDate(suggestion: SuggestedDate) {
    const d = new Date(suggestion.date);
    this.currentMonth = d.getMonth();
    this.currentYear = d.getFullYear();
    this.selectedDate = d.getDate();
    this.availableSlots = suggestion.slots;
    this.suggestedDates = [];
    this.selectedSlotTime = null;
  }

  get isScheduleValid(): boolean {
    const hasFocusSelection = this.isInviteFlow
      ? !!this.selectedType
      : this.selectedSkills.length > 0;
    return (
      !this.profileIncomplete && !!this.selectedDate && hasFocusSelection && !!this.selectedSlot
    );
  }

  get focusSummary(): string {
    return this.isInviteFlow
      ? this.selectedType?.name || ''
      : this.selectedSkills.map((s) => s.skill).join(', ');
  }

  /** Skills from booking context with the primary skill excluded, for the summary card. */
  get inviteFocusSkillsSummary(): string {
    const skills = this.bookingContext?.skills ?? [];
    const primary = this.bookingContext?.primarySkillName;
    const filtered = primary ? skills.filter((s) => s !== primary) : skills;
    return filtered.join(', ');
  }

  scheduleInterview() {
    if (this.profileIncomplete) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Profile Incomplete',
        detail: 'Please add your Technical Skills in your profile before booking an interview.',
        life: 8000,
      });
      return;
    }

    if (!this.selectedDate) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Date',
        detail: 'Please select a date',
      });
      return;
    }
    if (this.isInviteFlow ? !this.selectedType : this.selectedSkills.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Focus Skills',
        detail: this.isInviteFlow
          ? 'Please select a focus area'
          : 'Please select at least one focus skill',
      });
      return;
    }
    if (!this.selectedSlot) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Slot',
        detail: 'Please select a time slot',
      });
      return;
    }

    this.currentStep = 3;
    if (!this.isInviteFlow) {
      this.step3Loading = true;
      this.loadRealInterviewPrice();
      this.resetCoupon();
      this.loadAvailableCoupons();
      this.resetCoinRedeem();
      this.loadTotalCoins();
    }
  }

  step3Loading = false;

  realInterviewPrice: number | null = null;

  private loadRealInterviewPrice(): void {
    this.realInterviewPrice = null;
    this.interviewService.getInterviewPrice(this.selectedPanelLevel).subscribe({
      next: (res) => {
        this.realInterviewPrice = res?.amount ?? null;
        this.step3Loading = false;
      },
      error: () => {
        this.realInterviewPrice = null;
        this.step3Loading = false;
      },
    });
  }

  couponCodeInput = '';
  applyingCoupon = false;
  couponError: string | null = null;
  appliedCoupon: {
    couponCode: string;
    originalAmount: number;
    discountAmount: number;
    finalAmount: number;
  } | null = null;

  availableCoupons: AvailableCoupon[] = [];
  loadingCoupons = false;

  private resetCoupon(): void {
    this.couponCodeInput = '';
    this.applyingCoupon = false;
    this.couponError = null;
    this.appliedCoupon = null;
    this.availableCoupons = [];
  }

  private loadAvailableCoupons(): void {
    this.loadingCoupons = true;
    this.interviewService.getAvailableCoupons().subscribe({
      next: (coupons) => {
        this.availableCoupons = coupons || [];
        this.loadingCoupons = false;
      },
      error: () => {
        this.availableCoupons = [];
        this.loadingCoupons = false;
      },
    });
  }

  couponOffLabel(coupon: AvailableCoupon): string {
    if (coupon.type === 'PERCENTAGE') {
      return `${coupon.discountPercent ?? 0}% off`;
    }
    return `₹${coupon.discountRate ?? 0} off`;
  }

  useCoupon(coupon: AvailableCoupon): void {
    if (this.appliedCoupon || this.applyingCoupon) return;
    this.couponCodeInput = coupon.couponCode;
    this.applyCoupon();
  }

  applyCoupon(): void {
    const code = this.couponCodeInput?.trim();
    if (!code || this.applyingCoupon) return;

    this.applyingCoupon = true;
    this.couponError = null;

    this.interviewService.applyCoupon(code, this.selectedPanelLevel).subscribe({
      next: (res) => {
        this.applyingCoupon = false;
        if (res?.valid) {
          this.appliedCoupon = {
            couponCode: res.couponCode ?? code,
            originalAmount: res.originalAmount,
            discountAmount: res.discountAmount,
            finalAmount: res.finalAmount,
          };
          this.couponError = null;
        } else {
          this.appliedCoupon = null;
          this.couponError = res?.message || 'Invalid coupon code.';
        }
        this.evaluateCoinRedeem();
      },
      error: (err) => {
        this.applyingCoupon = false;
        this.appliedCoupon = null;
        this.couponError = InterviewService.extractErrorMessage(
          err,
          'Could not apply coupon. Please try again.',
        );
      },
    });
  }

  removeCoupon(): void {
    this.appliedCoupon = null;
    this.couponError = null;
    this.couponCodeInput = '';
    this.evaluateCoinRedeem();
  }
  readonly MIN_REDEEM_COINS = 1000;
  totalCoins: number | null = null;
  loadingCoins = false;
  redeemCoins = false;
  evaluatingCoinRedeem = false;
  coinsToRedeem: number | null = null;
  coinDiscount: number | null = null;

  get canRedeemCoins(): boolean {
    return (this.totalCoins ?? 0) >= this.MIN_REDEEM_COINS;
  }

  private get amountAfterCoupon(): number {
    return this.appliedCoupon
      ? this.appliedCoupon.finalAmount
      : (this.realInterviewPrice ?? this.priceForLevel(this.selectedPanelLevel));
  }

  get totalPayable(): number {
    const base = this.amountAfterCoupon;
    if (this.redeemCoins && this.coinDiscount) {
      return Math.max(0, base - this.coinDiscount);
    }
    return base;
  }

  private resetCoinRedeem(): void {
    this.totalCoins = null;
    this.loadingCoins = false;
    this.redeemCoins = false;
    this.coinsToRedeem = null;
    this.coinDiscount = null;
    this.evaluatingCoinRedeem = false;
  }

  private loadTotalCoins(): void {
    this.loadingCoins = true;
    this.interviewService.getCoinBalance().subscribe({
      next: (res) => {
        this.totalCoins = res?.coinBalance ?? 0;
        this.loadingCoins = false;
      },
      error: () => {
        this.totalCoins = null;
        this.loadingCoins = false;
      },
    });
  }

  toggleRedeemCoins(): void {
    if (!this.canRedeemCoins) return;
    this.redeemCoins = !this.redeemCoins;
    this.evaluateCoinRedeem();
  }

  private evaluateCoinRedeem(): void {
    if (!this.redeemCoins || !this.canRedeemCoins) {
      this.coinsToRedeem = null;
      this.coinDiscount = null;
      return;
    }
    this.evaluatingCoinRedeem = true;
    this.interviewService
      .previewRedeemCoins(this.selectedPanelLevel, this.appliedCoupon?.couponCode)
      .subscribe({
        next: (res: RedeemCoinsPreview) => {
          this.evaluatingCoinRedeem = false;
          if (res?.valid) {
            this.coinsToRedeem = res.coinsToRedeem;
            this.coinDiscount = res.discountAmount;
          } else {
            this.coinsToRedeem = null;
            this.coinDiscount = null;
          }
        },
        error: () => {
          this.evaluatingCoinRedeem = false;
          this.coinsToRedeem = null;
          this.coinDiscount = null;
        },
      });
  }

  confirmSchedule() {
    this.showConfirmDialog = false;
    this.scheduling = true;

    const slot = this.selectedSlot!;
    const startTime = slot.slot.split(' - ')[0];

    const dateObj = new Date(this.currentYear, this.currentMonth, this.selectedDate);
    const dateStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
    const dayStr = dateObj.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();

    if (this.isInviteFlow) {
      const req: InviteMatcherRequest = {
        token: this.token,
        title: this.selectedType!.name,
        date: dateStr,
        day: dayStr,
        startTime: startTime,
        filterId: this.selectedType!.id,
        status: 'SCHEDULED',
        panelLevel: this.selectedPanelLevel,
      };
      this.interviewService.matchInterviewByToken(req).subscribe({
        next: () => {
          this.scheduling = false;
          this.booked = true;
          sessionStorage.removeItem('invite_token');
          this.messageService.add({
            severity: 'success',
            summary: 'Interview Scheduled!',
            detail: 'Check your email for the confirmation and meeting link.',
            life: 5000,
          });
        },
        error: (err: any) => {
          this.scheduling = false;
          const detail = InterviewService.extractErrorMessage(
            err,
            'Could not book the interview. Please try again.',
          );
          this.messageService.add({
            severity: 'error',
            summary: 'Booking Failed',
            detail,
            life: 6000,
          });
        },
      });
    } else {
      const request: ScheduleInterviewRequest = {
        title: this.selectedSkills.map((s) => s.skill).join(', '),
        date: dateStr,
        day: dayStr,
        startTime: startTime,
        skillIds: this.selectedSkills.map((s) => s.id),
        status: 'SCHEDULED',
        panelLevel: this.selectedPanelLevel,
      };
      this.scheduling = false;
      this.payForInterviewThenBook(request);
    }
  }

  /** Pay-per-interview flow: creates a Cashfree order for the selected panel
   *  level, opens Cashfree Checkout, and only calls scheduleInterview() (with
   *  the resulting order id attached) once the checkout flow completes —
   *  the backend independently re-verifies the order was actually paid
   *  before the interview is scheduled (see CandidatePanelistMatcherController). */
  private payForInterviewThenBook(request: ScheduleInterviewRequest): void {
    this.payingForInterview = true;

    this.interviewService
      .createInterviewOrder(
        this.selectedPanelLevel,
        this.appliedCoupon?.couponCode,
        this.redeemCoins && this.canRedeemCoins,
      )
      .subscribe({
        next: (order) => {
          this.openCashfreeCheckoutForInterview(order, request);
        },
        error: (err: any) => {
          this.payingForInterview = false;
          const detail = InterviewService.extractErrorMessage(
            err,
            'Could not start payment. Please try again.',
          );
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail,
            life: 6000,
          });
        },
      });
  }

  private openCashfreeCheckoutForInterview(order: any, request: ScheduleInterviewRequest): void {
    if (typeof Cashfree === 'undefined') {
      this.payingForInterview = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Failed',
        detail: 'Payment service failed to load. Please refresh and try again.',
        life: 6000,
      });
      return;
    }

    const cashfree = Cashfree({ mode: this.cashfreeMode });
    cashfree
      .checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: '_modal',
      })
      .then((result: any) => {
        if (result?.error) {
          // Covers both an outright failure and the user closing the modal
          // without paying — either way there's nothing to book yet.
          this.payingForInterview = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: result.error.message || 'Your payment could not be completed.',
            life: 7000,
          });
          return;
        }
        // Cashfree has confirmed the charge -- lock the button now, before
        // the booking API call even starts, so a slow/failing booking call
        // never leaves a window where "Pay & Book" is clickable again.
        this.paymentCompleted = true;
        this.bookInterviewAfterPayment({ ...request }, order.order_id);
      })
      .catch(() => {
        this.payingForInterview = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Failed',
          detail: 'Your payment could not be completed.',
          life: 7000,
        });
      });
  }

  private bookInterviewAfterPayment(
    request: ScheduleInterviewRequest,
    cashfreeOrderId: string,
  ): void {
    request.cashfreeOrderId = cashfreeOrderId;

    this.scheduling = true;
    this.interviewService.scheduleInterview(request).subscribe({
      next: (res) => {
        this.scheduling = false;
        this.payingForInterview = false;
        this.booked = true;
        this.messageService.add({
          severity: 'success',
          summary: 'Interview Scheduled!',
          detail: `Your interview on ${request.date} at ${request.startTime} has been booked. Check your email for the meeting link.`,
          life: 5000,
        });
        // Confirms the spend explicitly, right here -- the balance shown
        // elsewhere (dashboard/Coins page) can look unchanged right after
        // this if the same booking also earned fresh engagement coins, so
        // this toast is the one place that unambiguously says the
        // redemption happened.
        if (res?.coinsRedeemed) {
          this.messageService.add({
            severity: 'info',
            summary: 'Coins Redeemed',
            detail: `${res.coinsRedeemed} coins redeemed (₹${res.coinDiscount ?? 0} off)${
              res.coinBalance != null ? `. New balance: ${res.coinBalance} coins.` : '.'
            }`,
            life: 6000,
          });
        }
        setTimeout(() => this.router.navigate(['/app/candidate']), 2500);
      },
      error: (err: any) => {
        this.scheduling = false;
        this.payingForInterview = false;
        const detail = InterviewService.extractErrorMessage(
          err,
          'Payment succeeded but booking failed. Please contact support with your order id: ' +
            cashfreeOrderId,
        );
        this.messageService.add({
          severity: 'error',
          summary: 'No Panelist Available',
          detail,
          life: 9000,
        });
      },
    });
  }

  private loadCashfreeScript(): void {
    if (document.getElementById('cashfree-script')) return;
    const script = document.createElement('script');
    script.id = 'cashfree-script';
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;
    document.head.appendChild(script);
  }

  // ── Request popup ──────────────────────────────────────────────────────
  get isRequestValid(): boolean {
    return !!this.requestDate && !!this.requestTime;
  }

  submitTimeSlotRequest() {
    if (!this.requestDate || !this.requestTime) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Incomplete',
        detail: 'Please fill in date and time',
      });
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: 'Requested',
      detail: 'Your time-slot request has been submitted',
    });
    this.showRequestPopup = false;
    this.requestDate = '';
    this.requestTime = '';
  }
}
